<template>
  <div class="note-survey">
    <div class="survey-card">
      <div class="survey-header">
        <h4>Encuesta de calidad</h4>
        <span v-if="survey" class="survey-date">{{ formatDate(survey.answered_at) }}</span>
      </div>

      <div v-if="loading" class="survey-status">Cargando encuesta…</div>
      <div v-else-if="loadError" class="survey-status">
        No se pudo cargar la encuesta.
        <button class="survey-retry" @click="fetchSurvey">Reintentar</button>
      </div>
      <div v-else-if="!survey" class="survey-status">
        El cliente aún no responde la encuesta de este servicio.
      </div>

      <template v-else>
        <div v-for="answer in survey.answers" :key="answer.key" class="survey-row">
          <p class="survey-question">{{ answer.text }}</p>
          <div class="survey-answer">
            <span class="survey-face" :style="{ color: scaleColor(answer.value) }">
              {{ scaleFace(answer.value) }}
            </span>
            <span class="survey-answer-label" :style="{ color: scaleColor(answer.value) }">
              {{ scaleLabel(answer.value) }}
            </span>
            <span class="survey-answer-value">({{ answer.value }}/5)</span>
          </div>
        </div>

        <div class="survey-comments">
          <p class="survey-comments-title">Comentarios del cliente</p>
          <p v-if="survey.comments" class="survey-comments-body">{{ survey.comments }}</p>
          <p v-else class="survey-comments-empty">Sin comentarios.</p>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

const apiDirectory = process.env.API_DIRECTORY;

const SCALE = {
  1: { face: '😠', label: 'Muy insatisfecho', color: '#D5171D' },
  2: { face: '🙁', label: 'Insatisfecho', color: '#F57C00' },
  3: { face: '😐', label: 'Neutral', color: '#C9A100' },
  4: { face: '🙂', label: 'Satisfecho', color: '#689F38' },
  5: { face: '😄', label: 'Muy satisfecho', color: '#2E7D32' },
};

export default {
  name: 'NoteSurvey',
  props: {
    noteIdent: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      survey: null,
      loading: true,
      loadError: false,
    };
  },
  created() {
    this.fetchSurvey();
  },
  methods: {
    scaleFace(value) {
      return SCALE[value]?.face || '—';
    },
    scaleLabel(value) {
      return SCALE[value]?.label || `Valor ${value}`;
    },
    scaleColor(value) {
      return SCALE[value]?.color || '#666666';
    },
    formatDate(iso) {
      if (!iso) return '';
      const date = new Date(iso);
      if (Number.isNaN(date.getTime())) return iso;
      return date.toLocaleString('es-MX', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    },
    async fetchSurvey() {
      this.loading = true;
      this.loadError = false;
      try {
        const { data } = await axios.get(
          `${apiDirectory}/notes/${this.noteIdent}/survey/`,
          { headers: { Authorization: 'Token ' + localStorage.getItem('authToken') } }
        );
        this.survey = data.survey;
      } catch (error) {
        this.loadError = true;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.note-survey {
  font-family: 'Poppins', sans-serif;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: calc(100vh - 150px);
  padding: 24px 0;
}

.survey-card {
  background: #ffffff;
  border: 1px solid #d5deea;
  border-radius: 12px;
  width: 100%;
  max-width: 760px;
  margin: 0 auto;
  padding: 0 0 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
}

.survey-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  background: #d6e6fa;
  border-bottom: 1px solid #a9c3e4;
  border-left: 4px solid #2f6fb3;
}

.survey-header h4 {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #1f3f66;
}

.survey-date {
  font-size: 12px;
  font-weight: 600;
  color: #3d5270;
}

.survey-status {
  color: #5b6b7c;
  font-size: 13px;
  text-align: center;
  padding: 28px 16px;
}

.survey-retry {
  margin-left: 8px;
  padding: 4px 12px;
  background: #1f3f66;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
}

.survey-row {
  margin: 12px 16px 0;
  padding: 12px 14px;
  background: #f8fbff;
  border: 1px solid #e4ebf4;
  border-radius: 10px;
}

.survey-question {
  margin: 0 0 6px;
  font-size: 13px;
  font-weight: 600;
  color: #1a1a1a;
}

.survey-answer {
  display: flex;
  align-items: center;
  gap: 8px;
}

.survey-face {
  font-size: 22px;
  line-height: 1;
}

.survey-answer-label {
  font-size: 13px;
  font-weight: 600;
}

.survey-answer-value {
  font-size: 12px;
  color: #999;
}

.survey-comments {
  margin: 12px 16px 8px;
  padding: 12px 14px;
  background: #ffffff;
  border: 1px solid #e4ebf4;
  border-radius: 10px;
}

.survey-comments-title {
  margin: 0 0 4px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #1f3f66;
}

.survey-comments-body {
  margin: 0;
  font-size: 13px;
  color: #1a1a1a;
  white-space: pre-wrap;
  word-break: break-word;
}

.survey-comments-empty {
  margin: 0;
  font-size: 13px;
  color: #999;
}
</style>
